import {
  ActionList, ActionListItem, AlertVariant,
  Bullseye,
  Button, Card,
  CardBody,
  Checkbox,
  DescriptionList,
  DescriptionListGroup,
  EmptyState,
  Flex, FlexItem,
  FormSelect, FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter, ModalHeader,
  ModalVariant,
  PageSection, Pagination,
  SearchInput,
  Tab, TabTitleText,
  Tabs, TextArea, TextInput, Toolbar, ToolbarContent, ToolbarGroup,
  ToolbarItem
} from '@patternfly/react-core';
import {
  CheckIcon, PencilAltIcon,
  SearchIcon,
  TimesIcon
} from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

// Models & Utils
import { CustomField } from '@app/model/CustomField';
import { CustomFieldValue } from '@app/model/CustomFieldValue';
import { INSTRUCTOR_STATUS, INSTRUCTOR_TYPE } from '@app/model/Enums';
import { Instructor, skillRefLabel } from '@app/model/Instructor';
import { AUTOMATION_CERTIFICATION_PROCESS, BASE_CERTIFICATION_VERIFY_URL } from '@app/utils/config';
import { ddMMyyyyFormat, getBoolean } from '@app/utils/functions';

// Contexts & Components
import { AutomationApi } from '@app/api';
import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { AddressAsReceived } from '@app/components/AddressAsReceived';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { FormAddress } from '@app/components/FormAddress';
import { InlineEdit } from '@app/components/InLineEdit';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { isNotFound } from '@app/utils/ApiError';
import { RemoveButton } from '@app/components/RemoveButton'; // <-- Import the existing component
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { Ref } from '@app/model/History';
import { Skill } from '@app/model/Skill';
import { useClientExecutiveStore, useCountryStore, useCustomFieldStore, useInstructorStore, usePartnerStore } from '@app/store/entityStore';
import { useNotification } from '../AppLayout/NotificationContext';


// --- SUB-COMPONENT 1: SKILLS TABLE ---
const InstructorSkillsTable: React.FC<{ skills: Skill[] }> = ({ skills }) => {
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(5);
  const [filterText, setFilterText] = React.useState('');
  const [showCourse, setShowCourse] = React.useState(true);
  const [showCert, setShowCert] = React.useState(true);
  const [sortIndex, setSortIndex] = React.useState<number | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | null>(null);

  const filtered = React.useMemo(() => {
    return skills.filter(s => {
      const matchesText = !filterText ||
        s.course?.sku?.toLowerCase().includes(filterText.toLowerCase()) ||
        s.course?.name?.toLowerCase().includes(filterText.toLowerCase());
      const isCert = !!s.certification;
      const matchesType = (isCert && showCert) || (!isCert && showCourse);
      return matchesText && matchesType;
    });
  }, [skills, filterText, showCourse, showCert]);

  const sorted = React.useMemo(() => {
    if (sortIndex === null || sortDirection === null) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortIndex) {
        case 0: aVal = `${a.course?.sku} ${a.course?.name}`.toLowerCase(); bVal = `${b.course?.sku} ${b.course?.name}`.toLowerCase(); break;
        case 1: aVal = a.certification ? 'Cert' : 'Course'; bVal = b.certification ? 'Cert' : 'Course'; break;
        case 2: aVal = a.grantedDate ? new Date(a.grantedDate).getTime() : 0; bVal = b.grantedDate ? new Date(b.grantedDate).getTime() : 0; break;
        case 3: aVal = a.expirationDate ? new Date(a.expirationDate).getTime() : 0; bVal = b.expirationDate ? new Date(b.expirationDate).getTime() : 0; break;
        default: return 0;
      }
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [filtered, sortIndex, sortDirection]);

  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  return (
    <Card isPlain>
      <CardBody>
        <Toolbar id="skills-toolbar">
          <ToolbarContent>
            <ToolbarGroup variant="filter-group">
              <ToolbarItem>
                <SearchInput placeholder="Search SKU or Name..." value={filterText} onChange={(_e, v) => { setFilterText(v); setPage(1); }} onClear={() => setFilterText('')} />
              </ToolbarItem>
              <ToolbarItem>
                <Flex gap={{ default: 'gapMd' }}>
                  <Checkbox label="Courses" isChecked={showCourse} id="filter-course" onChange={(_e, v) => { setShowCourse(v); setPage(1); }} />
                  <Checkbox label="Certifications" isChecked={showCert} id="filter-cert" onChange={(_e, v) => { setShowCert(v); setPage(1); }} />
                </Flex>
              </ToolbarItem>
            </ToolbarGroup>
            <ToolbarItem variant="pagination">
              <Pagination isCompact itemCount={filtered.length} page={page} perPage={perPage} onSetPage={(_e, p) => setPage(p)} onPerPageSelect={(_e, pp) => setPerPage(pp)} />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        <Table aria-label="Skills List">
          <Thead>
            <Tr>
              <Th sort={{ sortBy: { index: sortIndex ?? undefined, direction: sortDirection ?? undefined }, onSort: (_e, i, d) => { setSortIndex(i); setSortDirection(d); }, columnIndex: 0 }}>Name</Th>
              <Th sort={{ sortBy: { index: sortIndex ?? undefined, direction: sortDirection ?? undefined }, onSort: (_e, i, d) => { setSortIndex(i); setSortDirection(d); }, columnIndex: 1 }}>Type</Th>
              <Th sort={{ sortBy: { index: sortIndex ?? undefined, direction: sortDirection ?? undefined }, onSort: (_e, i, d) => { setSortIndex(i); setSortDirection(d); }, columnIndex: 2 }}>Granted</Th>
              <Th sort={{ sortBy: { index: sortIndex ?? undefined, direction: sortDirection ?? undefined }, onSort: (_e, i, d) => { setSortIndex(i); setSortDirection(d); }, columnIndex: 3 }}>Expiry</Th>
              <Th>Attendance</Th>
              <Th>Ready</Th>
              <Th>Certified</Th>
              <Th>Level</Th>
              <Th>LMS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginated.length > 0 ? paginated.map((row, i) => (
              <Tr key={i}>
                <Td>{row.course.sku} - {row.course.name}</Td>
                <Td>{row.certification === true ? 'Certification' : 'Course'}</Td>
                <Td>{ddMMyyyyFormat(row.grantedDate)}</Td>
                <Td>{ddMMyyyyFormat(row.expirationDate)}</Td>
                <Td>{ddMMyyyyFormat(row.courseAttendance)}</Td>
                <Td>{row.deliveryReady === true ? 'Yes' : 'No '}</Td>
                <Td>{row.certified === true ? 'Yes' : 'No '}</Td>
                <Td>{row.level}</Td>
                <Td>{row.levelLMS}</Td>
              </Tr>
            )) : (
              <Tr><Td colSpan={9}><Bullseye><EmptyState titleText="No skills found" icon={SearchIcon} /></Bullseye></Td></Tr>
            )}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

// --- SUB-COMPONENT 2: CUSTOM FIELDS TABLE ---
interface CFProps {
  instructorFields: CustomFieldValue[];
  availableDefinitions: CustomField[];
  onUpdate: (newFields: CustomFieldValue[]) => void;
}

const InstructorCustomFieldsTable: React.FC<CFProps> = ({ instructorFields, availableDefinitions, onUpdate }) => {
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(5);
  const [editingField, setEditingField] = React.useState<CustomFieldValue | null>(null);
  const [isAdding, setIsAdding] = React.useState(false);

  const paginated = instructorFields.slice((page - 1) * perPage, page * perPage);

  const onSave = () => {
    if (!editingField) return;
    let updated;
    if (isAdding) {
      updated = [...instructorFields, editingField];
    } else {
      updated = instructorFields.map(f => f.id === editingField.id || f.customField?.id === editingField.customField?.id ? editingField : f);
    }
    onUpdate(updated);
    setEditingField(null);
    setIsAdding(false);
  };

  const renderInput = (cfv: CustomFieldValue, setCfv: (v: CustomFieldValue) => void) => {
    const def = cfv.customField;
    if (!def) return null;
    const common = { isRequired: true, value: cfv.value || '' };

    switch (def.type) {
      case 'text': return <TextInput {...common} onChange={(_e, v) => setCfv({ ...cfv, value: v })} />;
      case 'textarea': return <TextArea {...common} onChange={(_e, v) => setCfv({ ...cfv, value: v })} />;
      case 'select':
        return (
          <FormSelect {...common} onChange={(_e, v) => setCfv({ ...cfv, value: v })}>
            <FormSelectOption label="Select..." value="" isDisabled />
            {def.values?.split('\n').filter(Boolean).map((v, i) => <FormSelectOption key={i} value={v} label={v} />)}
          </FormSelect>
        );
      case 'checkbox':
        return def.values?.split('\n').filter(Boolean).map(v => (
          <Checkbox key={v} id={v} label={v} isChecked={cfv.value?.includes(v)} onChange={(_e, checked) => {
            const current = cfv.value?.split('\n').filter(Boolean) || [];
            const next = checked ? [...current, v] : current.filter(x => x !== v);
            setCfv({ ...cfv, value: next.join('\n') + '\n' });
          }} />
        ));
      default: return null;
    }
  };

  return (
    <Card isPlain>
      <CardBody>
        <Toolbar id="cf-toolbar">
          <ToolbarContent>
            <ToolbarItem><Button variant="primary" onClick={() => { setEditingField({} as any); setIsAdding(true); }}>Add Field</Button></ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination isCompact itemCount={instructorFields.length} page={page} perPage={perPage} onSetPage={(_e, p) => setPage(p)} onPerPageSelect={(_e, pp) => setPerPage(pp)} />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        <Table>
          <Thead><Tr><Th>Field Name</Th><Th>Value</Th><Th>Actions</Th></Tr></Thead>
          <Tbody>
            {paginated.map((row, i) => (
              editingField && (editingField.id === row.id || editingField.customField?.id === row.customField?.id) && !isAdding ? (
                <Tr key="edit">
                  <Td>{row.customField?.name}</Td>
                  <Td>{renderInput(editingField, setEditingField)}</Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button icon={<CheckIcon />} onClick={onSave} variant="plain" />
                      <Button icon={<TimesIcon />} onClick={() => setEditingField(null)} variant="plain" />
                    </div>
                  </Td>
                </Tr>
              ) : (
                <Tr key={i}>
                  <Td>{row.customField?.name}</Td>
                  <Td>{row.value}</Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button icon={<PencilAltIcon />} variant="plain" onClick={() => setEditingField(row)} />
                      {/* NEW: Replaced Modal state and Button with <RemoveButton> */}
                      <RemoveButton 
                        confirmationMessage={`Confirm removal of ${row.customField?.name}?`}
                        onRemoveConfirm={() => onUpdate(instructorFields.filter(f => f !== row))}
                      />
                    </div>
                  </Td>
                </Tr>
              )
            ))}
            {isAdding && editingField && (
              <Tr key="new">
                <Td>
                  <FormSelect value={editingField.customField?.id || ''} onChange={(_e, v) => setEditingField({ ...editingField, customField: availableDefinitions.find(d => d.id === +v)! })}>
                    <FormSelectOption label="Select field..." value="" isDisabled />
                    {availableDefinitions.map(d => <FormSelectOption key={d.id} value={d.id} label={d.name} />)}
                  </FormSelect>
                </Td>
                <Td>{renderInput(editingField, setEditingField)}</Td>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button icon={<CheckIcon />} onClick={onSave} variant="plain" isDisabled={!editingField.customField || !editingField.value} />
                    <Button icon={<TimesIcon />} onClick={() => { setEditingField(null); setIsAdding(false); }} variant="plain" />
                  </div>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

// --- MAIN COMPONENT ---
const InstructorDetail: React.FunctionComponent = () => {
  type Action = { type: 'load' | 'update', payload: Instructor };

  const [isDirty, setIsDirty] = React.useState(false);

  // This blocks navigation within the app if the form is dirty
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const [instructor, setInstructor] = React.useReducer((state: Instructor, action: Action) => {
    if (action.type === 'load') return action.payload;
    if (action.type === 'update') return action.payload; // Side effect removed from here
    return state;
  }, {} as Instructor);

  const [isEditEnabled, setIsEditEnabled] = React.useState(false);
  const [activeTabKey, setActiveTabKey] = React.useState<string | number>(0);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  /** No instructor answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState(false);

  // Show the RHN ID in the breadcrumb rather than the internal database id.
  useBreadcrumbLabel(instructor.rhnId);
  
  // Data
  const {items: countries} = useEntity(useCountryStore);
  const {items: partners} = useEntity(usePartnerStore);
  const {items: clientExecutives} = useEntity(useClientExecutiveStore);
  const {items: customFields} = useEntity(useCustomFieldStore);

  const loadInstructor = useInstructorStore(state => state.loadById);
  const saveInstructor = useInstructorStore(state => state.save);
  const removeInstructor = useInstructorStore(state => state.delete);

  const updateInstructor = React.useCallback((updated: Instructor) => {
    setIsDirty(true); // Mark as dirty whenever an update happens
    setInstructor({ type: 'update', payload: updated });
  }, []);

// --- 1. Data Fetching Effect (Only runs when ID changes) ---
  React.useEffect(() => {
    let isMounted = true;
    
    const loadAllData = async () => {
      setNotFound(false);
      beginLoading();
      try {
        if (id !== 'new') { 
          const instData = await loadInstructor(id as string);
          const safeWorkingCopy = structuredClone(instData);
          setInstructor({ type: 'load', payload: safeWorkingCopy });
        } else { 
          setIsEditEnabled(true); 
          setInstructor({ type: 'load', payload: { customFields: [], skills: [] } as any }); 
        }
      } catch (e: any) {
        // An id matching no instructor is the answer rather than a failure: the page renders it.
        // (The finally below still runs and ends the loading backdrop.)
        if (!isMounted) return;
        if (isNotFound(e)) setNotFound(true);
        else addNotification(e.message, AlertVariant.danger);
      } finally {
        // Using requestAnimationFrame ensures the spinner disappears only after 
        // the browser has finished painting the new data.
        if (isMounted) requestAnimationFrame(() => setTimeout(() => endLoading(), 0));
        else endLoading();
      }
    };
    loadAllData();
    return () => { 
      isMounted = false; 
    };
  }, [id]);

  // --- 2. Browser Guard Effect (Only runs when isDirty changes) ---
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Standard for modern browsers to trigger the alert
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]); // Only re-binds when the dirty state toggles

  const onSaveClick = async () => {
    beginLoading();
    try {
      const isNew = id === 'new' ? true : false;
      saveInstructor(instructor, isNew);

      addNotification("Instructor saved successfully!", AlertVariant.success); 
      setIsDirty(false);
    } catch (e: any) { 
      addNotification(e.message, AlertVariant.danger); 
    }
    finally { 
      endLoading(); 
    }
  };

  const onCancelClick = async () => {
    setIsDirty(false);
    
    if (id === 'new') {
      setInstructor({ type: 'load', payload: { customFields: [], skills: [] } as any });
    } else {
      // Instantly grabs the un-mutated, pristine object from the cache and clones it
      const pristineData = await loadInstructor(id as string);
      setInstructor({ type: 'load', payload: structuredClone(pristineData) });
    }
  }

  const handleTriggerCertUpdate = async () => {
    if (!instructor.certificationId) {
      addNotification(
        "Missing Certification ID", 
        AlertVariant.warning, 
        "Cannot trigger update: This instructor does not have a Certification ID."
      );
      return;
    }

    beginLoading();
    try {
      await AutomationApi.triggerProcess(AUTOMATION_CERTIFICATION_PROCESS, instructor.certificationId);
      addNotification(
        "Process Triggered", 
        AlertVariant.success, 
        "The certification update process went through successfully."
      );
    } catch (error: any) {
      addNotification(
        "Error Triggering Process", 
        AlertVariant.danger, 
        error.message || "An unexpected error occurred while communicating with the server."
      );
    } finally {
      endLoading();
    }
  };

  const onRefresh = async () => {
    if (id === 'new') return;
    const fresh = await loadInstructor(id as string, true);
    setInstructor({ type: 'load', payload: structuredClone(fresh) });
    setIsDirty(false);
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removeInstructor(instructor.id);
      addNotification("Instructor removed successfully!", AlertVariant.success);
      // Cleared so the unsaved-changes blocker does not fight the navigation
      setIsDirty(false);
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/instructors', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger, "Error removing instructor");
    } finally {
      endLoading();
    }
  };

  /** Names a skill the change log could only report as an id - see skillRefLabel. */
  const resolveSkillRef = React.useCallback(
    (field: string, ref: Ref) => (field === 'skills' ? skillRefLabel(instructor.skills, ref.id) : undefined),
    [instructor.skills]
  );

  const renderStatus = (labelText: string) => {
    switch (labelText) {
      case 'ACTIVE': return <Label color="green">{labelText}</Label>;
      case 'PENDING': return <Label color="orange">{labelText}</Label>;
      case 'REDHAT': return <Label color="blue">{labelText}</Label>;
      case 'INACTIVE': return <Label color="red">{labelText}</Label>;
      case 'NOT_ENROLLED': return <Label color="grey">{labelText}</Label>;
      default: return <>{labelText}</>;
    }
  };

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="instructor"
        identifier={id}
        backTo={{ path: '/instructors', label: 'Back to instructors' }}
      />
    );
  }

  if (!instructor.id && id !== 'new') return null;

  return (
    <EntityChangeLog
      entity="instructor"
      id={instructor.id}
      entityLabel="this instructor"
      title={instructor.name}
      resolveRef={resolveSkillRef}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={instructor.name || 'New Instructor'}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" isDisabled={!isDirty} onClick={onSaveClick}>Save</Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" isDisabled={!isDirty} onClick={onCancelClick}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              {/* Instructor removal is not released yet: the option stays visible but inactive. */}
              <PageActionsMenu
                items={[
                  {
                    key: 'cert-update',
                    label: 'Trigger Certification Update',
                    onClick: handleTriggerCertUpdate
                  },
                  {
                    key: 'change-log',
                    label: 'Change log',
                    onClick: () => setChangeLogOpen(true),
                    hasDividerBefore: true
                  }
                ]}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled
                removeConfirmationMessage={`Confirm removal of "${instructor.name}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      />

      <PageSection isWidthLimited>
        <DescriptionList columnModifier={{ default: '3Col' }}>
          <DescriptionListGroup><InlineEdit label='Name' value={instructor.name} type='TextInput' onSave={v => updateInstructor({ ...instructor, name: v as string })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Type' value={instructor.type} type='Select' options={Object.values(INSTRUCTOR_TYPE).map(v => ({ value: v, label: v }))} onSave={v => updateInstructor({ ...instructor, type: v as INSTRUCTOR_TYPE })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Certification #' value={instructor.certificationId} type='TextInput' onSave={v => updateInstructor({ ...instructor, certificationId: v as string })} isGlobalEdition={isEditEnabled} displayAs='Link' href={BASE_CERTIFICATION_VERIFY_URL + instructor.certificationId} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='RHN ID' value={instructor.rhnId} type='TextInput' onSave={v => updateInstructor({ ...instructor, rhnId: v as string })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='E-mail' value={instructor.email} type='TextInput' onSave={v => updateInstructor({ ...instructor, email: v as string })} displayAs='Link' href={'mailto:' + instructor.email} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Red Hat E-mail' value={instructor.redhatEmail} type='TextInput' onSave={v => updateInstructor({ ...instructor, redhatEmail: v as string })} displayAs='Link' href={'mailto:' + instructor.redhatEmail} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Phone Number' value={instructor.mobileNumber} type='Telephone' onSave={v => updateInstructor({ ...instructor, mobileNumber: v as string })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label="RHCX" value={instructor.rhcx ? instructor.rhcx.toString() : 'false'} type='Checkbox' onSave={v => updateInstructor({ ...instructor, rhcx: getBoolean(v) })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Status' value={instructor.status} type='Select' options={Object.values(INSTRUCTOR_STATUS).map(v => ({ value: v, label: v }))} onSave={v => updateInstructor({ ...instructor, status: v as INSTRUCTOR_STATUS })} decorator={renderStatus} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Activation Date' value={ddMMyyyyFormat(instructor.activationDate)} type='Date' onSave={v => updateInstructor({ ...instructor, activationDate: v as Date })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Deactivation Date' value={ddMMyyyyFormat(instructor.deactivationDate)} type='Date' onSave={v => updateInstructor({ ...instructor, deactivationDate: v as Date })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='RHCI Attendance Date' value={ddMMyyyyFormat(instructor.rhciAttendanceDate)} type='Date' onSave={v => updateInstructor({ ...instructor, rhciAttendanceDate: v as Date })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='RHCI Virtual Attendance Date' value={ddMMyyyyFormat(instructor.rhciVirtualAttendanceDate)} type='Date' onSave={v => updateInstructor({ ...instructor, rhciVirtualAttendanceDate: v as Date })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Name on LMS' value={instructor.nameLMS} type='TextInput' onSave={v => updateInstructor({ ...instructor, nameLMS: v as string })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label="Nationality" type='FilterableSelect' value={instructor.nacionality?.alpha2 ?? ''} decorator={() => instructor.nacionality ? `${instructor.nacionality.flag} ${instructor.nacionality.name}` : ''} onSave={v => updateInstructor({ ...instructor, nacionality: countries.find(c => c.alpha2 === v)! })} options={countries.map(c => ({ value: c.alpha2, content: c.flag + ' ' + c.name, selected: c.alpha2 === instructor.nacionality?.alpha2 }))} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Passport #' value={instructor.passportNumber} type='TextInput' onSave={v => updateInstructor({ ...instructor, passportNumber: v as string })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Passport Expiration Date' value={ddMMyyyyFormat(instructor.passportExpirationDate)} type='Date' onSave={v => updateInstructor({ ...instructor, passportExpirationDate: v as Date })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Passport Authorizing Country' value={instructor.passportAuthoriCountry?.alpha2 ?? ''} decorator={() => instructor.passportAuthoriCountry?.name ?? ''} type='FilterableSelect' onSave={v => updateInstructor({ ...instructor, passportAuthoriCountry: countries.find(c => c.alpha2 === v)! })} options={countries.map(c => ({ value: c.alpha2, content: c.flag + ' ' + c.name, selected: c.alpha2 === instructor.passportAuthoriCountry?.alpha2 }))} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Daily Rate' value={instructor.dailyRate} type='TextInput' onSave={v => updateInstructor({ ...instructor, dailyRate: v as number })} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Partner' value={instructor.partner?.id ?? ''} decorator={() => instructor.partner?.name ?? ''} type='FilterableSelect' onSave={v => updateInstructor({ ...instructor, partner: partners.find(p => p.id === +v)! })} options={partners.map(p => ({ value: p.id, content: p.name, selected: p.id === instructor.partner?.id }))} isGlobalEdition={isEditEnabled} /></DescriptionListGroup>
          <DescriptionListGroup>
            <Flex direction={{ default: 'column' }}>
              <Flex><FlexItem><strong>Home Address</strong></FlexItem></Flex>
              <Flex><FlexItem><FormAddress value={instructor.homeAddress} id="address" name="address" onChange={(_e, v) => updateInstructor({ ...instructor, homeAddress: v })} /></FlexItem></Flex>
              {/* Collapsed, and that matters here: until this instructor's next message from the
                  feed, this and homeAddress.line1 hold the same blob - changelog 0040 backfilled
                  the text without clearing the lines, so that the readable copy survived. */}
              <Flex><FlexItem><AddressAsReceived value={instructor.homeAddressText} /></FlexItem></Flex>
            </Flex>
          </DescriptionListGroup>
          <DescriptionListGroup><InlineEdit label='Client Executive' type='FilterableSelect' value={instructor.clientExecutive?.id ?? ''} decorator={() => instructor.clientExecutive?.name ?? ''} onSave={v => updateInstructor({ ...instructor, clientExecutive: clientExecutives.find(i => i.id === v) })} options={clientExecutives.map(i => ({ value: i.id as number, content: i.name as string, selected: i.id === instructor.clientExecutive?.id }))} /></DescriptionListGroup>
        </DescriptionList>
      </PageSection>

      <PageSection isWidthLimited>
        <Tabs activeKey={activeTabKey} onSelect={(_, k) => setActiveTabKey(k)}>
          <Tab eventKey={0} title={<TabTitleText>Skills List</TabTitleText>}>
            <InstructorSkillsTable skills={instructor.skills || []} />
          </Tab>
          <Tab eventKey={1} title={<TabTitleText>Custom Fields</TabTitleText>}>
            <InstructorCustomFieldsTable
              instructorFields={instructor.customFields || []}
              availableDefinitions={customFields}
              onUpdate={(newFields) => updateInstructor({ ...instructor, customFields: newFields })}
            />
          </Tab>
        </Tabs>
      </PageSection>

      {/* Navigation Warning Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()} // Cancels the navigation
      >
        <ModalHeader title="Unsaved Changes" titleIconVariant="warning" />
        <ModalBody>
        You have unsaved changes in the instructor profile. If you leave this page, your progress will be lost.
        </ModalBody>
        <ModalFooter>
          <Button key="proceed" variant="danger" onClick={() => blocker.proceed?.()}>
            Leave and lose changes
          </Button>,
          <Button key="cancel" variant="link" onClick={() => blocker.reset?.()}>
            Stay on page
          </Button>
        </ModalFooter>
      </Modal>
    </EntityChangeLog>
  );
};

export { InstructorDetail };
